import { Capacitor } from '@capacitor/core'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'

export type CaptureResult =
  | { ok: true; dataUrl: string }
  | { ok: false; reason: 'cancelled' | 'permission' | 'unavailable' | 'error'; detail?: string }

const CAPTURE_TIMEOUT_MS = 120_000

function classifyCaptureError(error: unknown): CaptureResult {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : String(error)
  const lower = message.toLowerCase()
  if (/cancel|dismiss|abort|user cancelled/.test(lower)) return { ok: false, reason: 'cancelled' }
  if (/permission|denied|access|authorized|privacy|not allowed/.test(lower)) {
    return { ok: false, reason: 'permission', detail: message }
  }
  if (/not implemented|plugin.*not|unavailable/.test(lower)) {
    return { ok: false, reason: 'unavailable', detail: message }
  }
  return { ok: false, reason: 'error', detail: message }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('Camera timed out')), ms)
    promise
      .then((value) => {
        window.clearTimeout(timer)
        resolve(value)
      })
      .catch((error) => {
        window.clearTimeout(timer)
        reject(error)
      })
  })
}

/** Hidden file input when Camera plugin UI does not appear (web / edge cases). */
function pickImageViaFileInput(): Promise<CaptureResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = 'environment'
    input.style.display = 'none'
    const cleanup = () => input.remove()

    input.addEventListener('change', () => {
      const file = input.files?.[0]
      cleanup()
      if (!file) {
        resolve({ ok: false, reason: 'cancelled' })
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : null
        if (!dataUrl) {
          resolve({ ok: false, reason: 'error', detail: 'Could not read image' })
          return
        }
        resolve({ ok: true, dataUrl })
      }
      reader.onerror = () => resolve({ ok: false, reason: 'error', detail: 'Could not read image' })
      reader.readAsDataURL(file)
    })

    document.body.appendChild(input)
    input.click()
  })
}

async function ensureCameraPermissions(): Promise<CaptureResult | null> {
  if (!Capacitor.isNativePlatform()) return null

  if (!Capacitor.isPluginAvailable('Camera')) {
    return {
      ok: false,
      reason: 'unavailable',
      detail: 'Camera plugin is not linked in this build. Reinstall from TestFlight after the next update.',
    }
  }

  try {
    let status = await Camera.checkPermissions()
    if (status.camera === 'denied' && status.photos === 'denied') {
      return {
        ok: false,
        reason: 'permission',
        detail: 'Camera and photo access are off in Settings.',
      }
    }

    if (status.camera !== 'granted' || (status.photos !== 'granted' && status.photos !== 'limited')) {
      status = await Camera.requestPermissions({ permissions: ['camera', 'photos'] })
    }

    if (status.camera === 'denied' && status.photos === 'denied') {
      return { ok: false, reason: 'permission' }
    }
  } catch (error) {
    return classifyCaptureError(error)
  }

  return null
}

async function captureNative(): Promise<CaptureResult> {
  const blocked = await ensureCameraPermissions()
  if (blocked) return blocked

  try {
    const shot = await withTimeout(
      Camera.getPhoto({
        quality: 85,
        width: 1800,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        promptLabelHeader: 'Scan a bill',
        promptLabelPhoto: 'Choose from library',
        promptLabelPicture: 'Take photo',
        correctOrientation: true,
      }),
      CAPTURE_TIMEOUT_MS,
    )
    if (!shot.dataUrl) return { ok: false, reason: 'cancelled' }
    return { ok: true, dataUrl: shot.dataUrl }
  } catch (error) {
    return classifyCaptureError(error)
  }
}

async function captureWeb(): Promise<CaptureResult> {
  if (Capacitor.isPluginAvailable('Camera')) {
    try {
      const shot = await withTimeout(
        Camera.getPhoto({
          quality: 85,
          width: 1800,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt,
          webUseInput: true,
          promptLabelHeader: 'Scan a bill',
          promptLabelPhoto: 'Choose from library',
          promptLabelPicture: 'Take photo',
        }),
        CAPTURE_TIMEOUT_MS,
      )
      if (!shot.dataUrl) return { ok: false, reason: 'cancelled' }
      return { ok: true, dataUrl: shot.dataUrl }
    } catch {
      // Fall through to plain file input
    }
  }
  return pickImageViaFileInput()
}

export async function captureBillImage(): Promise<CaptureResult> {
  if (Capacitor.getPlatform() === 'web') {
    return captureWeb()
  }
  return captureNative()
}
